"use client";

import Image from "next/image";
import { useState } from "react";
import { useTranslations } from "next-intl";

const IMAGE_BASE =
	"/image/new-version/ValueAddedSolutions/industrial-automation/sourcing-for-the-automation-parts";

const TABS = [
	{
		id: "custom",
		tabKey: "partsSourcingCustomTab",
		titleKey: "partsSourcingCustomTitle",
		descriptionKeys: ["partsSourcingCustomDesc1", "partsSourcingCustomDesc2", "partsSourcingCustomDesc3"],
		images: [
			{
				src: `${IMAGE_BASE}/custom-precision-parts/custom-precision-parts-1.webp`,
				altKey: "partsSourcingCustomImageAlt1",
			},
			{
				src: `${IMAGE_BASE}/custom-precision-parts/custom-precision-parts-2.webp`,
				altKey: "partsSourcingCustomImageAlt2",
			},
			{
				src: `${IMAGE_BASE}/custom-precision-parts/custom-precision-parts-3.webp`,
				altKey: "partsSourcingCustomImageAlt3",
			},
		],
	},
	{
		id: "semiStandard",
		tabKey: "partsSourcingSemiStandardTab",
		titleKey: "partsSourcingSemiStandardTitle",
		descriptionKeys: ["partsSourcingSemiStandardDesc"],
		bulletKeys: [
			"partsSourcingSemiStandardItem1",
			"partsSourcingSemiStandardItem2",
			"partsSourcingSemiStandardItem3",
			"partsSourcingSemiStandardItem4",
			"partsSourcingSemiStandardItem5",
			"partsSourcingSemiStandardItem6",
			"partsSourcingSemiStandardItem7",
			"partsSourcingSemiStandardItem8",
			"partsSourcingSemiStandardItem9",
			"partsSourcingSemiStandardItem10",
			"partsSourcingSemiStandardItem11",
		],
		images: [
			{
				src: `${IMAGE_BASE}/semi-standard-parts/semi-standard-parts-1.webp`,
				altKey: "partsSourcingSemiStandardImageAlt1",
			},
			{
				src: `${IMAGE_BASE}/semi-standard-parts/semi-standard-parts-2.webp`,
				altKey: "partsSourcingSemiStandardImageAlt2",
			},
			{
				src: `${IMAGE_BASE}/semi-standard-parts/semi-standard-parts-3.webp`,
				altKey: "partsSourcingSemiStandardImageAlt3",
			},
		],
	},
	{
		id: "standard",
		tabKey: "partsSourcingStandardTab",
		titleKey: "partsSourcingStandardTitle",
		descriptionKeys: ["partsSourcingStandardDesc"],
		labeledBlocks: [
			{
				labelKey: "partsSourcingStandardCoreLabel",
				textKey: "partsSourcingStandardCoreText",
			},
			{
				labelKey: "partsSourcingStandardSupportLabel",
				textKey: "partsSourcingStandardSupportText",
			},
		],
		images: [
			{
				src: `${IMAGE_BASE}/standard-parts/standard-parts-1.webp`,
				altKey: "partsSourcingStandardImageAlt1",
			},
			{
				src: `${IMAGE_BASE}/standard-parts/standard-parts-2.webp`,
				altKey: "partsSourcingStandardImageAlt2",
			},
			{
				src: `${IMAGE_BASE}/standard-parts/standard-parts-3.webp`,
				altKey: "partsSourcingStandardImageAlt3",
			},
		],
	},
] as const;

export default function IndustrialAutomationPartsSourcing() {
	const t = useTranslations("new-industrial-automation");
	const [activeIndex, setActiveIndex] = useState(0);
	const activeTab = TABS[activeIndex];

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{t("partsSourcingTitle")}
					</h2>
					<div
						className="mt-5 flex flex-wrap justify-center gap-3 sm:mt-6 sm:gap-4"
						role="tablist"
						aria-label={t("partsSourcingTabsAriaLabel")}
					>
						{TABS.map((tab, index) => {
							const isActive = index === activeIndex;

							return (
								<button
									key={tab.id}
									type="button"
									role="tab"
									aria-selected={isActive}
									onClick={() => setActiveIndex(index)}
									className={`rounded border px-5 py-2 text-sm transition-colors sm:px-6 sm:text-base ${
										isActive
											? "border-themePrimary400 bg-themePrimary400 text-white"
											: "border-themePrimary400/40 bg-white text-themePrimary400 hover:border-themePrimary400 hover:bg-[#f5ffd4]"
									}`}
								>
									{t(tab.tabKey)}
								</button>
							);
						})}
					</div>
				</header>

				<div className="grid gap-8 lg:grid-cols-[1fr_0.95fr] lg:items-start lg:gap-12">
					<article>
						<h3 className="mb-4 text-xl font-bold leading-tight text-black sm:text-2xl md:text-[28px]">
							{t(activeTab.titleKey)}
						</h3>

						<div className="space-y-5 text-sm leading-relaxed text-black md:text-base">
							{activeTab.descriptionKeys?.map((key) => <p key={key}>{t(key)}</p>)}
						</div>

						{"bulletKeys" in activeTab && (
							<ul className="mt-5 grid gap-x-8 gap-y-3 text-sm leading-snug text-themePrimary400 sm:grid-cols-2 md:text-base">
								{activeTab.bulletKeys.map((key) => (
									<li key={key} className="flex gap-2">
										<span aria-hidden="true">•</span>
										<span>{t(key)}</span>
									</li>
								))}
							</ul>
						)}

						{"labeledBlocks" in activeTab && (
							<div className="mt-7 space-y-6 text-sm leading-relaxed text-black md:text-base">
								{activeTab.labeledBlocks.map((block) => (
									<p key={block.labelKey}>
										<strong>{t(block.labelKey)}</strong> {t(block.textKey)}
									</p>
								))}
							</div>
						)}
					</article>

					<div className="grid gap-4 sm:grid-cols-2">
						{activeTab.images.map((image, index) => (
							<div
								key={image.src}
								className={`relative overflow-hidden rounded-lg bg-[#eef3f9] ${
									index === 0 ? "aspect-[16/9] sm:col-span-2" : "aspect-[4/3]"
								}`}
							>
								<Image
									src={image.src}
									alt={t(image.altKey)}
									fill
									className="object-contain p-4"
									sizes={index === 0 ? "(max-width: 1024px) 100vw, 48vw" : "(max-width: 640px) 100vw, 24vw"}
								/>
							</div>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}
